// The per-kind score formulas (General tab): what ONE unit / building / city / tech is
// worth to its owner's score. Mirrors Rift.Sim.SimScore.Worth in the Client, which is the
// source of truth — the game evaluates these, the Editor only previews the result beside
// each Score field. Nothing here writes a Score field.
//
// Semantics, shared with the game:
//   blank formula   → the entity's own Score field IS its score (the pre-formula behaviour)
//   valid formula   → its value over SCORE (the Score field) and the kind's other variables:
//                     COST (gold price) for units, buildings and techs; TURNS (research
//                     turns) for techs; LEVEL (the city's current level) for cities, which
//                     are founded rather than bought and so have no COST
//   invalid formula → the game logs it once and every entity of that kind is worth 0

import {BuildingData, RemoteConstantsModel, UnitData} from "../models/remoteConstants.model";
import {evaluateFormula, isValidFormula} from "./formula";

export const SCORE_VARIABLE = 'SCORE';
export const COST_VARIABLE = 'COST';
export const TURNS_VARIABLE = 'TURNS';
export const LEVEL_VARIABLE = 'LEVEL';

// The variables each kind's formula may name.
export const ENTITY_SCORE_VARIABLES: readonly string[] = [SCORE_VARIABLE, COST_VARIABLE];
export const CITY_SCORE_VARIABLES: readonly string[] = [SCORE_VARIABLE, LEVEL_VARIABLE];
export const TECH_SCORE_VARIABLES: readonly string[] = [SCORE_VARIABLE, COST_VARIABLE, TURNS_VARIABLE];

// The game keeps scores in Q16 fixed point (1/65536 steps); a Score field within one step
// of the formula's value reconciles with it.
export const RECONCILE_TOLERANCE = 1 / 65536;

// How many city levels the readout previews when the City formula depends on LEVEL.
export const CITY_PREVIEW_LEVELS = 3;

export type ScoreKind = 'unit' | 'building' | 'city' | 'tech';

export const SCORE_KIND_LABEL: Record<ScoreKind, string> = {
    unit: 'Unit', building: 'Building', city: 'City', tech: 'Tech',
};

export type FormulaStatus = 'blank' | 'valid' | 'invalid';

export interface ScoreVariables {
    SCORE: number;
    COST?: number;
    TURNS?: number;
    LEVEL?: number;
}

// What the game will use for one entity, next to what its Score field says.
export interface EffectiveScore {
    kind: ScoreKind;
    status: FormulaStatus;
    formula: string;
    // The Score field's value (a tier's Player Score for techs).
    field: number;
    // What the entity is worth in game: the field for a blank formula, the formula's value,
    // or 0 for an invalid formula. For a city, its worth at the level it is founded with.
    value: number;
    // False when the field and the value disagree.
    reconciles: boolean;
    // Cities only, and only when the formula makes the worth depend on LEVEL: the worth at
    // the first CITY_PREVIEW_LEVELS levels from the founding level up.
    perLevel?: { level: number, value: number }[];
}

export function isCityBuilding(building: BuildingData): boolean {
    return (building.properties?.startingSlots ?? 0) > 0;
}

export function scoreFormulaFor(state: RemoteConstantsModel, kind: ScoreKind): string {
    switch (kind) {
        case 'unit': return state.unitScoreFormula ?? '';
        case 'building': return state.buildingScoreFormula ?? '';
        case 'city': return state.cityScoreFormula ?? '';
        case 'tech': return state.techScoreFormula ?? '';
    }
}

export function scoreVariablesFor(kind: ScoreKind): readonly string[] {
    switch (kind) {
        case 'tech': return TECH_SCORE_VARIABLES;
        case 'city': return CITY_SCORE_VARIABLES;
        default: return ENTITY_SCORE_VARIABLES;
    }
}

export function formulaStatus(formula: string, variableNames: readonly string[]): FormulaStatus {
    if (formula.trim().length === 0) return 'blank';
    return isValidFormula(formula, variableNames) ? 'valid' : 'invalid';
}

// The formula's value for one entity, or the field / 0 for a blank / invalid formula.
function worth(status: FormulaStatus, formula: string, variables: ScoreVariables): number {
    if (status === 'blank') return variables.SCORE;
    if (status === 'invalid') return 0;
    const values: Record<string, number> = {[SCORE_VARIABLE]: variables.SCORE};
    if (variables.COST !== undefined) values[COST_VARIABLE] = variables.COST;
    if (variables.TURNS !== undefined) values[TURNS_VARIABLE] = variables.TURNS;
    if (variables.LEVEL !== undefined) values[LEVEL_VARIABLE] = variables.LEVEL;
    return evaluateFormula(formula, values) ?? 0;
}

export function effectiveScore(kind: ScoreKind, formula: string, variables: ScoreVariables): EffectiveScore {
    const status = formulaStatus(formula, scoreVariablesFor(kind));
    const field = variables.SCORE;
    const value = worth(status, formula, variables);
    const effective: EffectiveScore = {
        kind, status, formula, field, value,
        reconciles: Math.abs(value - field) < RECONCILE_TOLERANCE,
    };

    if (kind === 'city' && status === 'valid' && variables.LEVEL !== undefined) {
        const first = variables.LEVEL;
        const perLevel = Array.from({length: CITY_PREVIEW_LEVELS}, (_, i) => {
            const level = first + i;
            return {level, value: worth(status, formula, {...variables, LEVEL: level})};
        });
        if (perLevel.some(p => Math.abs(p.value - value) >= RECONCILE_TOLERANCE)) effective.perLevel = perLevel;
    }

    return effective;
}

export function effectiveUnitScore(state: RemoteConstantsModel, unit: UnitData): EffectiveScore {
    return effectiveScore('unit', scoreFormulaFor(state, 'unit'), {SCORE: unit.score ?? 0, COST: unit.price ?? 0});
}

// A city (Starting Slots > 0) reads the City Score Formula at the level it is founded with
// (its Start Level — what SimSpawner gives it); any other building reads the Building one.
export function effectiveBuildingScore(state: RemoteConstantsModel, building: BuildingData): EffectiveScore {
    if (isCityBuilding(building)) {
        return effectiveScore('city', scoreFormulaFor(state, 'city'), {SCORE: building.score ?? 0, LEVEL: building.startLevel ?? 0});
    }
    return effectiveScore('building', scoreFormulaFor(state, 'building'), {SCORE: building.score ?? 0, COST: building.price ?? 0});
}

// A tier the price / research tables do not reach reads 0 for the value they lack, as in game.
export function effectiveTechScore(state: RemoteConstantsModel, tier: number): EffectiveScore {
    const config = state.techConfig;
    return effectiveScore('tech', scoreFormulaFor(state, 'tech'), {
        SCORE: config?.playerScores?.[tier] ?? 0,
        COST: config?.prices?.[tier] ?? 0,
        TURNS: config?.researchTimes?.[tier] ?? 0,
    });
}

// "15", "7.5", "0.33" — enough digits to read, no float noise.
export function formatScore(value: number): string {
    return Number(value.toFixed(2)).toString();
}

// The readout's text: the in-game worth, per level for a level-dependent city, or the
// invalid-formula note.
export function describeWorth(effective: EffectiveScore): string {
    if (effective.status === 'invalid') return '0 (invalid formula)';
    if (effective.perLevel) return effective.perLevel.map(p => `L${p.level} ${formatScore(p.value)}`).join(' · ');
    return formatScore(effective.value);
}

// One or two sentences saying where the readout comes from; the readout's tooltip.
export function describeEffectiveScore(effective: EffectiveScore): string {
    const label = SCORE_KIND_LABEL[effective.kind];
    const lower = label.toLowerCase();
    switch (effective.status) {
        case 'blank':
            return `No ${label} Score Formula is set on the General tab, so the Player Score field is what this ${lower} is worth in game.`;
        case 'invalid':
            return `The ${label} Score Formula "${effective.formula}" is not valid, so every ${lower} is worth 0 in game until it is fixed on the General tab.`;
        case 'valid': {
            const levels = effective.perLevel
                ? ` It depends on the city's level: ${effective.perLevel.map(p => `${formatScore(p.value)} at level ${p.level}`).join(', ')}.`
                : '';
            const field = effective.reconciles
                ? ' This matches the Player Score field.'
                : ` The Player Score field says ${formatScore(effective.field)}; the game uses the formula.`;
            return `${label} Score Formula "${effective.formula}" makes this ${lower} worth ${formatScore(effective.value)} in game.${levels}${field}`;
        }
    }
}
